import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { BuildingThreeIcon, StarIcon, UserTwoIcon } from '@repo/ui/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { diffuseOtoIndividualPeriodsEndOfStayAppreciations } from '../../api/end-of-journey-reviews/sharing/diffuse-oto-individual-periods-end-of-stay-appreciations'
import { diffuseOtoIndividualPropositionsToOl } from '../../api/enrollments/diffuse-oto-individual-propositions-to-ol'
import { getPeriodsPlacementInfo } from '../../api/periods/get-periods-placement-info'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { OtoIndividualPeriodsFilter } from '../../components/pages/oto-individual/oto-individual-periods-filter'
import { getOtoIndividualPlacePeriodsColumns } from '../../components/pages/oto-individual/utils'
import { Paths } from '../../lib/constants/path'
import useAuth from '../../lib/hooks/use-auth'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import { PeriodFilter, PeriodTypes } from '../../lib/types/models/period/period'
import {
  transformOtoIndividualEnrollmentsFiltersToUrl,
  transformUrlToOtoIndividualEnrollmentsFilters
} from '../../lib/utils/filters-mapping/oto-individual-enrollments'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'

export function OtoIndividualPlacePeriodsListPage() {
  const { t } = useTranslate()
  const { user } = useAuth()
  const { navigateWithEvent } = useNavigator()
  const tableRef = useRef<DynamicTableRef<PeriodTypes.OtoIndividual>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<PeriodFilter>({
      initialFilters: { year: new Date().getFullYear() },
      options: { allowSearchParam: true },
      transformFilterToUrl: transformOtoIndividualEnrollmentsFiltersToUrl,
      transformUrlToFilter: transformUrlToOtoIndividualEnrollmentsFilters
    })

  const isAdmin = user.role === RoleEnum.ADMIN
  const columns = getOtoIndividualPlacePeriodsColumns(t, user.role)

  const { data: placementInfo, isFetching: isPlacementInfoLoading } = useQuery({
    queryKey: ['periods-placement-info', selectedFilter],
    queryFn: () => getPeriodsPlacementInfo(selectedFilter)
  })

  const diffuseToOlMutation = useMutation({
    mutationKey: ['diffuse-oto-individual-propositions-to-ol'],
    mutationFn: diffuseOtoIndividualPropositionsToOl,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.clearSelection()
      toast.success(t('oto.individual.periods.diffuse.to.ol.success'))
    }
  })

  // "Notes/Satisfaction": the provider sees the end-of-stay ratings of the youths of the périodes
  const diffuseEndOfStayAppreciationsMutation = useMutation({
    mutationKey: ['diffuse-oto-individual-periods-end-of-stay-appreciations'],
    mutationFn: diffuseOtoIndividualPeriodsEndOfStayAppreciations,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.clearSelection()
      toast.success(t('oto.individual.placements.end.of.stay.appreciations.diffused.success'))
    }
  })

  return (
    <div>
      <UserNavBar
        title={t('oto.individual.title')}
        icon={isAdmin ? BuildingThreeIcon : UserTwoIcon}
      />
      <div className={'h-4'} />

      <DynamicDataTable<PeriodTypes.OtoIndividual, PeriodFilter, undefined, 'OTO_INDIVIDUAL'>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={t('oto.individual.periods.table.title')}
        columns={columns}
        isSearchable={false}
        selectable={isAdmin}
        label={() => (
          <Badge
            variant="orange"
            loading={isPlacementInfoLoading}
            text={t('oto.individual.periods.placement.info', {
              total: placementInfo?.total ?? 0,
              placed: placementInfo?.placed ?? 0,
              unplaced: placementInfo?.unplaced ?? 0
            })}
          />
        )}
        allowSearchParam
        endpoint={API.periods.getAll.url}
        version={'OTO_INDIVIDUAL'}
        sorting={{ id: 'startDate', desc: false }}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        permissions={{ delete: false }}
        onRowClick={(period, event) =>
          navigateWithEvent(
            Paths.OTO_INDIVIDUAL_PLACES_BY_PERIOD_LIST_PAGE.replace(':id', period.id.toString()),
            event
          )
        }
        actions={
          isAdmin
            ? [
                {
                  label: t('oto.individual.periods.actions.diffuse.to.ol'),
                  type: 'multiple',
                  variant: 'ghost',
                  refetch: true,
                  requiredConfirmation: true,
                  cancelText: t('button.close'),
                  confirmationMessage: t('oto.individual.periods.actions.diffuse.to.ol.confirm'),
                  fn: (rows) =>
                    diffuseToOlMutation.mutateAsync({ ids: rows.map((period) => period.id) })
                },
                {
                  label: 'Notes/Satisfaction',
                  icon: StarIcon,
                  variant: 'ghost',
                  type: 'multiple',
                  refetch: true,
                  fn: (rows) =>
                    diffuseEndOfStayAppreciationsMutation.mutateAsync({
                      ids: rows.map((period) => period.id),
                      diffused: true
                    })
                }
              ]
            : undefined
        }
      />

      {/* Filters Drawer */}
      <FilterDrawer<PeriodFilter>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <OtoIndividualPeriodsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
